<template>
    <!--
        Start/end time inputs for DateRangePicker (one "Time" input in single
        mode). Times go in and out as 24-hour "HH:mm" strings; `timeFormat` only
        changes how they are shown and entered. Emits `update` with
        { startTime, endTime }.
    -->
    <div    class="drp-time-fields flex flex-wrap gap-4">
        <label  v-for="field in fields"
                :key="field.key"
                class="flex flex-1 flex-col gap-1"
        >
            <span   class="text-sm text-gray-600">{{ field.label }}</span>
            <vue-datepicker time-picker
                            auto-apply
                            :clearable="false"
                            :enable-seconds="false"
                            :format="displayFormat"
                            hide-input-icon
                            :is-24="timeFormat !== '12h'"
                            :model-value="pickerValue(field.key)"
                            :teleport="true"
                            :ui="{ input: inputClasses }"
                            @update:model-value="value => onUpdate(field.key, value)"
            ></vue-datepicker>
        </label>
    </div>
</template>

<script>
import VueDatepicker from '@vuepic/vue-datepicker'
import { parseTime, timeFromParts } from '../../../utils/dateRangePresets'

export default {
    name: 'DateRangeTimeFields',
    components: { VueDatepicker },
    emits: ['update'],
    props: {
        mode: {
            type: String,
            default: 'range',
        },
        startTime: {
            type: String,
            required: true,
        },
        endTime: {
            type: String,
            required: true,
        },
        // '24h' or '12h'.
        timeFormat: {
            type: String,
            default: '24h',
        },
    },
    data() {
        return {
            inputClasses: 'w-full rounded-md border border-gray-300 px-3 py-2 text-base text-gray-800',
        }
    },
    computed: {
        fields() {
            if (this.mode === 'single') {
                return [{ key: 'startTime', label: 'Time' }]
            }
            return [
                { key: 'startTime', label: 'Start time' },
                { key: 'endTime', label: 'End time' },
            ]
        },
        // date-fns tokens, which the picker uses for its input text.
        displayFormat() {
            return (this.timeFormat === '12h') ? 'h:mm a' : 'HH:mm'
        },
    },
    methods: {
        pickerValue(key) {
            return parseTime(this[key])
        },
        onUpdate(key, value) {
            if (!value || (value.hours === undefined)) {
                return
            }
            const time = timeFromParts(value.hours, value.minutes ?? 0)
            const times = { startTime: this.startTime, endTime: this.endTime, [key]: time }
            // Single mode is one moment, so its one input sets both.
            if (this.mode === 'single') {
                times.endTime = time
            }
            this.$emit('update', times)
        },
    },
}
</script>

<style scoped>
/* The picker card sets vue-datepicker's border color to transparent for the
   calendar; give the time inputs a visible border back. */
.drp-time-fields :deep(.dp__input) {
    border-color: #d1d5db;
    text-align: left;
}

.drp-time-fields :deep(.dp__input:hover) {
    border-color: #9ca3af;
}
</style>
